import { supabase } from '../config/supabase';
import { FrontendSMS, SendSMSRequest, SendSMSResponse, SendHireSMSRequest, SendHireSMSResponse, SendBulkHireSMSRequest, SendBulkHireSMSResponse } from '../types/sms';
import { smsApiService } from './smsApiService';
import { frontendSMSService } from './frontendSMSService';

export const messageService = {
  /**
   * Get all SMS messages for the current user's organization
   * Only owners and admins can access this
   */
  async getMessages(): Promise<FrontendSMS[]> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can view messages');
      }

      // First get all leads for the organization to get their phone numbers
      const { data: leads, error: leadsError } = await supabase
        .from('pre_onboarding_leads')
        .select('phone_number')
        .eq('org_id', profile.org_id);

      if (leadsError) {
        throw leadsError;
      }

      // Extract phone numbers from leads
      const phoneNumbers = leads
        ?.map(lead => lead.phone_number)
        .filter(phone => phone) || []; // Filter out null/undefined phone numbers

      if (phoneNumbers.length === 0) {
        return [];
      }

      // Fetch messages for those phone numbers (outbound messages use recipient's number)
      // Supabase has a default limit of 1000 rows - we need to get ALL messages
      // Use pagination to fetch all messages
      let allMessages: any[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;
      
      while (hasMore) {
        const { data, error, count } = await supabase
          .from('frontend_sms')
          .select('*', { count: 'exact' })
          .in('number', phoneNumbers)
          .order('created_at', { ascending: true })
          .range(from, from + pageSize - 1);
        
        if (error) {
          throw error;
        }
        
        if (data && data.length > 0) {
          allMessages = allMessages.concat(data);
        }
        
        // Check if we've fetched all messages
        if (!data || data.length < pageSize || (count !== null && allMessages.length >= count)) {
          hasMore = false;
        } else {
          from += pageSize;
        }
      }
      
      return allMessages;

    } catch (error: any) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  /**
   * Get messages for a specific lead
   */
  async getMessagesForLead(leadId: string): Promise<FrontendSMS[]> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can view messages');
      }

      // First verify the lead belongs to the user's organization and get phone number
      const { data: lead, error: leadError } = await supabase
        .from('pre_onboarding_leads')
        .select('id, org_id, phone_number')
        .eq('id', leadId)
        .eq('org_id', profile.org_id)
        .single();

      if (leadError || !lead) {
        throw new Error('Lead not found or access denied');
      }

      if (!lead.phone_number) {
        return []; // No phone number, no messages
      }

      // Fetch messages for the specific lead's phone number
      const { data, error } = await supabase
        .from('frontend_sms')
        .select('*')
        .eq('number', lead.phone_number)
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      return data || [];

    } catch (error: any) {
      console.error('Error fetching messages for lead:', error);
      throw error;
    }
  },

  /**
   * Send an SMS message
   */
  async sendSMS(request: SendSMSRequest): Promise<SendSMSResponse> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role, full_name')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can send messages');
      }

      // Verify the lead belongs to the user's organization
      const { data: lead, error: leadError } = await supabase
        .from('pre_onboarding_leads')
        .select('id, org_id, name, email, phone_number')
        .eq('id', request.leadId)
        .eq('org_id', profile.org_id)
        .single();

      if (leadError || !lead) {
        throw new Error('Lead not found or access denied');
      }

      // Send SMS via API
      const smsResult = await smsApiService.sendSMS(request.phoneNumber, request.message);
      
      if (!smsResult.success) {
        throw new Error(smsResult.error || 'Failed to send SMS');
      }

      // Log the outbound SMS using the new service
      let smsData = null;
      try {
        smsData = await frontendSMSService.logOutboundSMS(
          lead.name,
          request.phoneNumber,
          request.message,
          0 // cost - you might want to calculate this based on message length
        );
        console.log('SMS logged successfully:', smsData);
      } catch (logError) {
        console.error('Error logging SMS:', logError);
        // Don't throw here - SMS was sent successfully, just logging failed
      }

      return {
        success: true,
        message: 'SMS sent successfully',
        smsId: smsData?.id
      };

    } catch (error: any) {
      console.error('Error sending SMS:', error);
      return {
        success: false,
        message: 'Failed to send SMS',
        error: error.message
      };
    }
  },

  /**
   * Get message statistics for the organization
   */
  async getMessageStats(): Promise<{
    totalMessages: number;
    inboundMessages: number;
    outboundMessages: number;
    totalCost: number;
  }> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can view statistics');
      }

      // First get all leads for the organization to get their phone numbers
      const { data: leads, error: leadsError } = await supabase
        .from('pre_onboarding_leads')
        .select('phone_number')
        .eq('org_id', profile.org_id);

      if (leadsError) {
        throw leadsError;
      }

      if (!leads || leads.length === 0) {
        return {
          totalMessages: 0,
          inboundMessages: 0,
          outboundMessages: 0,
          totalCost: 0
        };
      }

      // Extract phone numbers from leads
      const phoneNumbers = leads
        .map(lead => lead.phone_number)
        .filter(phone => phone); // Filter out null/undefined phone numbers

      if (phoneNumbers.length === 0) {
        return {
          totalMessages: 0,
          inboundMessages: 0,
          outboundMessages: 0,
          totalCost: 0
        };
      }

      // Get message statistics for those phone numbers
      const { data, error } = await supabase
        .from('frontend_sms')
        .select('direction, cost')
        .in('number', phoneNumbers);

      if (error) {
        throw error;
      }

      const messages = data || [];
      const totalMessages = messages.length;
      const inboundMessages = messages.filter(m => m.direction === 'inbound').length;
      const outboundMessages = messages.filter(m => m.direction === 'outbound').length;
      const totalCost = messages.reduce((sum, m) => sum + (m.cost || 0), 0);

      return {
        totalMessages,
        inboundMessages,
        outboundMessages,
        totalCost
      };

    } catch (error: any) {
      console.error('Error fetching message statistics:', error);
      throw error;
    }
  },

  /**
   * Mark messages as seen for a specific lead
   */
  async markMessagesAsSeen(leadId: string): Promise<void> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can mark messages as seen');
      }

      // Get the lead's phone number
      const { data: lead, error: leadError } = await supabase
        .from('pre_onboarding_leads')
        .select('phone_number')
        .eq('id', leadId)
        .eq('org_id', profile.org_id)
        .single();

      if (leadError || !lead) {
        throw new Error('Lead not found or access denied');
      }

      if (!lead.phone_number) {
        return; // No phone number, no messages to mark as seen
      }

      // Mark all messages for this phone number as seen
      const { error: updateError } = await supabase
        .from('frontend_sms')
        .update({ seen: true })
        .eq('number', lead.phone_number)
        .eq('seen', false);

      if (updateError) {
        throw updateError;
      }

    } catch (error: any) {
      console.error('Error marking messages as seen:', error);
      throw error;
    }
  },

  /**
   * Get SMS costs aggregated by org_id
   */
  async getSMSCostsByOrg(): Promise<{ [orgId: string]: number }> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can view SMS costs');
      }

      // Get all leads for the organization to get their phone numbers
      const { data: leads, error: leadsError } = await supabase
        .from('pre_onboarding_leads')
        .select('phone_number, org_id')
        .eq('org_id', profile.org_id);

      if (leadsError) {
        throw leadsError;
      }

      if (!leads || leads.length === 0) {
        return { [profile.org_id]: 0 };
      }

      // Extract phone numbers from leads
      const phoneNumbers = leads
        .map(lead => lead.phone_number)
        .filter(phone => phone);

      if (phoneNumbers.length === 0) {
        return { [profile.org_id]: 0 };
      }

      // Get SMS costs for those phone numbers
      const { data: messages, error: messagesError } = await supabase
        .from('frontend_sms')
        .select('cost, number')
        .in('number', phoneNumbers);

      if (messagesError) {
        throw messagesError;
      }

      // Aggregate costs by org_id
      const costsByOrg: { [orgId: string]: number } = {};
      const totalCost = (messages || []).reduce((sum, msg) => sum + (msg.cost || 0), 0);
      costsByOrg[profile.org_id] = totalCost;

      return costsByOrg;

    } catch (error: any) {
      console.error('Error fetching SMS costs by org:', error);
      throw error;
    }
  },

  /**
   * Get message history for a single new_hire from the frontend_sms table.
   * Hires are not authenticated users — they only exist in new_hire.
   * Messages are matched by the hire's mobileNumber.
   * Returns messages ordered by frontend_sms.created_at descending (newest first).
   */
  async getMessagesForHire(hireId: string): Promise<FrontendSMS[]> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('User not authenticated');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();
      if (profileError || !profile) throw new Error('Could not fetch user profile');
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can view hire messages');
      }

      let hireQuery = supabase
        .from('new_hire')
        .select('id, org_id, fullName, mobileNumber, recruiter_emails')
        .eq('id', hireId)
        .eq('org_id', profile.org_id);

      if (profile.role === 'admin' && user.email) {
        hireQuery = hireQuery.ilike('recruiter_emails', `%${user.email}%`);
      }

      const { data: hire, error: hireError } = await hireQuery.single();
      if (hireError || !hire) throw new Error('Hire not found or access denied');
      if (!hire.mobileNumber) return [];

      // Build possible phone number formats for matching
      const digits = hire.mobileNumber.replace(/\D/g, '');
      const possibleNumbers: string[] = [hire.mobileNumber];
      if (digits.length === 10) {
        possibleNumbers.push(`+1${digits}`);
      } else if (digits.length === 11 && digits.startsWith('1')) {
        possibleNumbers.push(`+${digits}`);
      }
      const uniqueNumbers = possibleNumbers.filter(
        (v, i, a) => a.indexOf(v) === i
      );

      const { data, error } = await supabase
        .from('frontend_sms')
        .select('*')
        .in('number', uniqueNumbers)
        .order('created_at', { ascending: false }); // newest first
      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching messages for hire:', error);
      throw error;
    }
  },

  /**
   * Get ALL hire conversations for the current org.
   * Fetches all new_hires with phone numbers, collects matching frontend_sms rows,
   * and returns them grouped by hire, newest first.
   */
  async getHireConversations(): Promise<{
    hire: { id: string; fullName: string; mobileNumber: string; email: string };
    messages: FrontendSMS[];
    latestMessageAt: string;
  }[]> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('User not authenticated');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();
      if (profileError || !profile) throw new Error('Could not fetch user profile');
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied');
      }

      // Get hires for this org; admins only see hires assigned to them
      let hiresQuery = supabase
        .from('new_hire')
        .select('id, fullName, mobileNumber, email, recruiter_emails')
        .eq('org_id', profile.org_id);

      if (profile.role === 'admin' && user.email) {
        hiresQuery = hiresQuery.ilike('recruiter_emails', `%${user.email}%`);
      }

      const { data: hires, error: hiresError } = await hiresQuery;
      if (hiresError) throw hiresError;

      const hiresWithPhones = (hires || []).filter(
        (h: any) => h.mobileNumber
      );
      if (hiresWithPhones.length === 0) return [];

      // Collect all possible phone number forms
      const numberToHireId: Record<string, string> = {};
      const allNumbers: string[] = [];
      for (const hire of hiresWithPhones) {
        const raw = hire.mobileNumber as string;
        const digits = raw.replace(/\D/g, '');
        const nums: string[] = [raw];
        if (digits.length === 10) nums.push(`+1${digits}`);
        else if (digits.length === 11 && digits.startsWith('1')) nums.push(`+${digits}`);
        for (const n of nums) {
          if (!numberToHireId[n]) {
            numberToHireId[n] = hire.id;
            allNumbers.push(n);
          }
        }
      }

      // Fetch all frontend_sms rows for those numbers, newest first.
      // Supabase has a default limit of 1000 rows per query, so use pagination
      // to ensure we truly fetch ALL messages for these hires.
      let allMessages: FrontendSMS[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error, count } = await supabase
          .from('frontend_sms')
          .select('*', { count: 'exact' })
          .in('number', allNumbers)
          .order('created_at', { ascending: false })
          .range(from, from + pageSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allMessages = allMessages.concat(data as FrontendSMS[]);
        }

        if (!data || data.length < pageSize || (count !== null && allMessages.length >= count)) {
          hasMore = false;
        } else {
          from += pageSize;
        }
      }

      // Group messages by hire
      const hireMessagesMap: Record<string, FrontendSMS[]> = {};
      for (const msg of allMessages) {
        const hireId = numberToHireId[msg.number];
        if (!hireId) continue;
        if (!hireMessagesMap[hireId]) hireMessagesMap[hireId] = [];
        hireMessagesMap[hireId].push(msg);
      }

      // Build result, sorted by latest message timestamp descending
      const hireMap: Record<string, any> = {};
      for (const h of hiresWithPhones) {
        hireMap[h.id] = h;
      }

      const result = Object.entries(hireMessagesMap)
        .map(([hireId, msgs]) => ({
          hire: {
            id: hireId,
            fullName: hireMap[hireId]?.fullName || 'Unknown',
            mobileNumber: hireMap[hireId]?.mobileNumber || '',
            email: hireMap[hireId]?.email || '',
          },
          messages: msgs, // already sorted descending
          latestMessageAt: msgs[0]?.created_at || '',
        }))
        .sort((a, b) =>
          new Date(b.latestMessageAt).getTime() - new Date(a.latestMessageAt).getTime()
        );

      return result;
    } catch (error: any) {
      console.error('Error fetching hire conversations:', error);
      throw error;
    }
  },

  /**
   * Send an SMS message to a hire
   */
  async sendHireSMS(request: SendHireSMSRequest): Promise<SendHireSMSResponse> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role, full_name')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Only owners can send messages to hires
      if (profile.role !== 'owner') {
        throw new Error('Access denied: Only owners can send messages to hires');
      }

      // Verify the hire belongs to the user's organization
      const { data: hire, error: hireError } = await supabase
        .from('new_hire')
        .select('id, org_id, fullName, email, mobileNumber')
        .eq('id', request.hireId)
        .eq('org_id', profile.org_id)
        .single();

      if (hireError || !hire) {
        throw new Error('Hire not found or access denied');
      }

      // Send SMS via API with hire-specific from number
      const HIRE_MESSENGER_FROM_NUMBER = '+12014222696'; // +1-207-560-8267 in E.164 format
      const smsResult = await smsApiService.sendSMS(request.phoneNumber, request.message, HIRE_MESSENGER_FROM_NUMBER);
      
      if (!smsResult.success) {
        throw new Error(smsResult.error || 'Failed to send SMS');
      }

      // Log the outbound SMS
      let smsData = null;
      try {
        smsData = await frontendSMSService.logOutboundSMS(
          hire.fullName,
          request.phoneNumber,
          request.message,
          0 // cost
        );
        console.log('Hire SMS logged successfully:', smsData);
      } catch (logError) {
        console.error('Error logging hire SMS:', logError);
        // Don't throw here - SMS was sent successfully, just logging failed
      }

      return {
        success: true,
        message: 'SMS sent successfully',
        smsId: smsData?.id
      };

    } catch (error: any) {
      console.error('Error sending hire SMS:', error);
      return {
        success: false,
        message: 'Failed to send SMS',
        error: error.message
      };
    }
  },

  /**
   * Send SMS messages to multiple hires (bulk send)
   */
  async sendBulkHireSMS(request: SendBulkHireSMSRequest): Promise<SendBulkHireSMSResponse> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role, full_name')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Only owners can send bulk messages to hires
      if (profile.role !== 'owner') {
        throw new Error('Access denied: Only owners can send bulk messages to hires');
      }

      // Fetch all hires to verify they belong to the organization
      const { data: hires, error: hiresError } = await supabase
        .from('new_hire')
        .select('id, org_id, fullName, email, mobileNumber')
        .in('id', request.hireIds)
        .eq('org_id', profile.org_id);

      if (hiresError) {
        throw new Error('Error fetching hires');
      }

      if (!hires || hires.length === 0) {
        throw new Error('No hires found or access denied');
      }

      // Filter hires that have phone numbers
      const hiresWithPhones = hires.filter(hire => hire.mobileNumber);

      if (hiresWithPhones.length === 0) {
        throw new Error('No hires with phone numbers found');
      }

      // Send SMS to each hire individually with hire-specific from number
      const HIRE_MESSENGER_FROM_NUMBER = '+12014222696'; // +1-207-560-8267 in E.164 format
      const results = await Promise.allSettled(
        hiresWithPhones.map(async (hire) => {
          try {
            const smsResult = await smsApiService.sendSMS(hire.mobileNumber, request.message, HIRE_MESSENGER_FROM_NUMBER);
            
            if (!smsResult.success) {
              throw new Error(smsResult.error || 'Failed to send SMS');
            }

            // Log the outbound SMS
            let smsData = null;
            try {
              smsData = await frontendSMSService.logOutboundSMS(
                hire.fullName,
                hire.mobileNumber,
                request.message,
                0 // cost
              );
            } catch (logError) {
              console.error(`Error logging SMS for hire ${hire.id}:`, logError);
            }

            return {
              hireId: hire.id,
              success: true,
              smsId: smsData?.id
            };
          } catch (error: any) {
            console.error(`Error sending SMS to hire ${hire.id}:`, error);
            return {
              hireId: hire.id,
              success: false,
              error: error.message
            };
          }
        })
      );

      // Process results
      const processedResults = results.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          return {
            hireId: hiresWithPhones[index]?.id || request.hireIds[index],
            success: false,
            error: result.reason?.message || 'Unknown error'
          };
        }
      });

      const successCount = processedResults.filter(r => r.success).length;
      const failCount = processedResults.filter(r => !r.success).length;

      return {
        success: failCount === 0,
        message: `Sent ${successCount} message(s)${failCount > 0 ? `, ${failCount} failed` : ''}`,
        results: processedResults
      };

    } catch (error: any) {
      console.error('Error sending bulk hire SMS:', error);
      return {
        success: false,
        message: 'Failed to send bulk SMS',
        results: request.hireIds.map(id => ({
          hireId: id,
          success: false,
          error: error.message
        }))
      };
    }
  }
};

export default messageService;
